<h2 mat-dialog-title class="sk-dialog-title">
  <mat-icon>assignment</mat-icon> Tagesbericht
  <span class="tb-title-spacer"></span>
  <span class="tb-status-chip" [attr.data-status]="report ? status : 'New'">
    <mat-icon class="tb-status-icon" *ngIf="readOnly && !loadError">lock</mat-icon>{{ statusLabel }}
  </span>
</h2>

<mat-dialog-content class="tb-content">
  <!-- Kopf: Einsatz-Kontext -->
  <div class="tb-head sk-card">
    <div class="tb-head-title">
      <strong>{{ assignment.auftragTitle || assignment.title }}</strong>
      <span class="sk-muted" *ngIf="assignment.customerName">{{ assignment.customerName }}</span>
    </div>
    <div class="tb-head-meta">
      <span *ngIf="!isMultiDay">
        <mat-icon class="sk-inline-icon">today</mat-icon>{{ workDate | date:'EEEE, dd.MM.yyyy' }}
      </span>
      <span>
        <mat-icon class="sk-inline-icon">schedule</mat-icon>{{ assignment.start | date:'HH:mm' }} – {{ assignment.end |
        date:'HH:mm' }}
      </span>
      <span *ngIf="assignment.location">
        <mat-icon class="sk-inline-icon">location_on</mat-icon>{{ assignment.location }}
      </span>
      <!-- SES-/IND-Nummer: Projekteigenschaft aus dem Auftrag, hier nur Anzeige. -->
      <span class="tb-referenz" *ngIf="orderNumber">
        <mat-icon class="sk-inline-icon">tag</mat-icon>
        <span class="sk-muted">{{ referenzLabel }}</span>
        <strong>{{ orderNumber }}</strong>
      </span>
      <span class="tb-referenz tb-referenz-fehlt" *ngIf="!orderNumber && !loading">
        <mat-icon class="sk-inline-icon">error_outline</mat-icon>
        Keine SES-/IND-Nummer im Projekt hinterlegt
      </span>
    </div>
    <!-- Tag des Berichts innerhalb des Einsatzzeitraums.
         Bewusst NICHT an readOnly gekoppelt: das Feld wechselt den Tag, es
         bearbeitet den Bericht nicht. Wäre es bei abgeschlossenem Bericht
         deaktiviert, käme man von einem fertigen Tag nicht mehr zu einem noch
         offenen. Gesperrt wird nur während Laden/Speichern. -->
    <mat-form-field appearance="outline" class="tb-date-field" *ngIf="isMultiDay">
      <mat-label>Datum</mat-label>
      <input matInput [matDatepicker]="workDatePicker" [(ngModel)]="workDateValue"
        [min]="minDateValue" [max]="maxDateValue" [disabled]="saving || loading" required>
      <mat-datepicker-toggle matIconSuffix [for]="workDatePicker"></mat-datepicker-toggle>
      <mat-datepicker #workDatePicker></mat-datepicker>
    </mat-form-field>
  </div>

  <div class="tb-hint offline" *ngIf="isOffline && !readOnly">
    <mat-icon>cloud_off</mat-icon>
    <span>Offline – der Tagesbericht kann erst wieder mit Verbindung gespeichert werden.</span>
  </div>

  <div class="tb-hint locked" *ngIf="readOnly && !loadError">
    <mat-icon>lock</mat-icon>
    <span>Der Tagesbericht ist abgeschlossen<ng-container *ngIf="submittedAt"> ({{ submittedAt | date:'dd.MM.yyyy HH:mm'
        }})</ng-container>
      und kann nicht mehr bearbeitet werden. Die Stunden stehen in der Stundenübersicht.</span>
  </div>

  <div class="tb-hint error" *ngIf="loadError">
    <mat-icon>error_outline</mat-icon>
    <span>{{ loadError }}</span>
  </div>

  <div class="tb-loading" *ngIf="loading">
    <mat-spinner diameter="28"></mat-spinner> <span class="sk-muted">Tagesbericht laden…</span>
  </div>

  <ng-container *ngIf="!loading && !loadError">
    <p class="sk-muted tb-empty" *ngIf="bereiche.length === 0">
      Noch kein Arbeitsbereich erfasst.
    </p>

    <!-- Arbeitsbereiche -->
    <section class="tb-bereich" *ngFor="let b of bereiche; let i = index; trackBy: trackByIndex"
      [class.has-errors]="showErrors && !readOnly && bereichErrors(b).length > 0">
      <div class="tb-bereich-head">
        <span class="tb-bereich-nr">Arbeitsbereich {{ i + 1 }}</span>
        <span class="sk-muted tb-bereich-hours">{{ formatHours(bereichHours(b)) }} h</span>
        <span class="tb-spacer"></span>
        <button mat-icon-button *ngIf="!readOnly" (click)="removeBereich(b)" [disabled]="saving"
          matTooltip="Arbeitsbereich löschen" aria-label="Arbeitsbereich löschen">
          <mat-icon>delete_outline</mat-icon>
        </button>
      </div>

      <!-- Die SES-/IND-Nummer steht am Projekt und wird hier nicht mehr erfasst.
           Historische Berichte mit abweichender Nummer zeigen sie weiterhin an. -->
      <div class="tb-bereich-referenz sk-muted" *ngIf="abweichendeReferenz(b) as alteReferenz">
        <mat-icon class="sk-inline-icon">history</mat-icon>
        Ursprünglich erfasst: {{ alteReferenz }}
      </div>

      <div class="tb-row">
        <mat-form-field appearance="outline" class="tb-ort">
          <mat-label>Ort</mat-label>
          <input matInput [(ngModel)]="b.ort" [readonly]="readOnly" placeholder="z. B. Bau 44 / EG">
        </mat-form-field>
      </div>

      <mat-form-field appearance="outline" class="tb-full">
        <mat-label>Ausgeführte Arbeiten</mat-label>
        <textarea matInput [(ngModel)]="b.beschreibung" [readonly]="readOnly" rows="3" cdkTextareaAutosize></textarea>
      </mat-form-field>

      <!-- Personen / Stunden -->
      <div class="tb-section-label">
        <mat-icon class="sk-inline-icon">groups</mat-icon> Personen / Stunden
      </div>

      <p class="sk-muted tb-empty" *ngIf="b.personen.length === 0">Noch keine Person.</p>

      <div class="tb-person" *ngFor="let p of b.personen; trackBy: trackByPerson">
        <span class="tb-person-name">{{ p.label }}</span>
        <mat-form-field appearance="outline" class="tb-hours">
          <mat-label>Std.</mat-label>
          <input matInput type="number" min="0" max="24" step="0.25" inputmode="decimal" [(ngModel)]="p.hours"
            [readonly]="readOnly">
          <span matSuffix>h</span>
        </mat-form-field>
        <button mat-icon-button *ngIf="!readOnly" (click)="removePerson(b, p)" [disabled]="saving"
          matTooltip="Person entfernen" aria-label="Person entfernen">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="tb-add-person" *ngIf="!readOnly">
        <mat-form-field appearance="outline" class="tb-add-select">
          <mat-label>Weitere Person</mat-label>

          <mat-select [(ngModel)]="b.addKey" (selectionChange)="addPerson(b)" [disabled]="saving">

            <mat-option *ngFor="let person of availablePeople(b)" [value]="personOptionKey(person)">
              {{ person.label }}
            </mat-option>

            <mat-option disabled *ngIf="availablePeople(b).length === 0">
              Keine weiteren Personen verfügbar
            </mat-option>

          </mat-select>

          <mat-icon matPrefix class="tb-add-icon">
            person_add
          </mat-icon>
        </mat-form-field>
      </div>


      <!-- ===================================================== -->
      <!-- MATERIAL: LV-POSITIONEN + FREIES MATERIAL             -->
      <!-- Preise werden hier bewusst nirgends angezeigt –       -->
      <!-- der Server liefert sie für den Tagesbericht gar nicht.-->
      <!-- ===================================================== -->

      <div class="tb-section-label tb-lv-heading">
        <mat-icon class="sk-inline-icon">format_list_numbered</mat-icon>
        Material
        <span class="sk-muted tb-lv-name" *ngIf="activeLv">· {{ activeLv.name }}</span>
      </div>

      <!-- ===================================================== -->
      <!-- Für diesen Einsatz GEPLANTES Material (SOLL).         -->
      <!-- Reine Vorgabe aus der Einsatzplanung: noch nicht      -->
      <!-- erfasst, nicht abrechnungswirksam. Über              -->
      <!-- "Planmengen übernehmen" wandern die Mengen in das     -->
      <!-- tatsächliche Material; die Planzeilen bleiben dabei   -->
      <!-- unverändert stehen.                                   -->
      <!-- ===================================================== -->
      <div class="tb-plan" *ngIf="geplantesMaterial.length > 0">
        <div class="tb-plan-head">
          <span class="tb-plan-title">
            <mat-icon class="sk-inline-icon">playlist_add_check</mat-icon>
            Geplantes Material
          </span>

          <button mat-stroked-button type="button" *ngIf="!readOnly"
                  (click)="planmengenUebernehmen(b)"
                  [disabled]="saving || uebernehmbarePlanmengen.length === 0"
                  matTooltip="Planmengen als tatsächliche Mengen übernehmen – vorhandene Positionen bleiben unverändert">
            <mat-icon>download</mat-icon>
            Planmengen übernehmen
          </button>
        </div>

        <div class="tb-plan-row" *ngFor="let plan of geplantesMaterial">
          <span class="tb-plan-name">
            <strong>{{ plan.nummer }}</strong>
            {{ planLabel(plan) }}
          </span>
          <span class="tb-plan-menge">
            <ng-container *ngIf="(plan.planMenge ?? 0) > 0; else ohneMenge">
              {{ plan.planMenge }} {{ plan.einheit }}
            </ng-container>
            <ng-template #ohneMenge>
              <span class="sk-muted">Menge offen</span>
            </ng-template>
          </span>
          <span class="tb-plan-status sk-muted" *ngIf="planPositionUebernommen(b, plan)">
            <mat-icon class="sk-inline-icon">check</mat-icon> erfasst
          </span>
        </div>

        <p class="sk-muted tb-plan-hint">
          Planmengen sind eine Vorgabe aus der Einsatzplanung und zählen erst als
          verbraucht, wenn sie unten als Material erfasst sind.
        </p>
      </div>

      <!-- Ausgewählte LV-Positionen -->
      <div class="tb-lv-selected" *ngFor="let lv of b.lvPositionen">

        <div class="tb-lv-selected-info">
          <div class="tb-lv-title">
            <strong>{{ lv.nummer }}</strong>
            <span class="tb-lv-haupttext">{{ lvHauptText(lv) }}</span>
          </div>
          <div class="tb-lv-meta" *ngIf="lvZusatzText(lv) as zusatz">
            <span>{{ zusatz }}</span>
          </div>
        </div>

        <mat-form-field appearance="outline" class="tb-lv-menge">
          <mat-label>Menge</mat-label>
          <input matInput type="number" min="0.0001" step="0.01" inputmode="decimal"
                 [(ngModel)]="lv.menge" [readonly]="readOnly" [disabled]="saving">
          <span matSuffix *ngIf="lv.einheit">{{ lv.einheit }}</span>
        </mat-form-field>

        <button mat-icon-button *ngIf="!readOnly" type="button" (click)="removeLvPosition(b, lv)"
                [disabled]="saving" matTooltip="LV-Position entfernen" aria-label="LV-Position entfernen">
          <mat-icon>delete_outline</mat-icon>
        </button>
      </div>

      <!-- Freies Material -->
      <div class="tb-frei-row" *ngFor="let material of b.freieMaterialien">

        <mat-form-field appearance="outline" class="tb-frei-bezeichnung">
          <mat-label>Bezeichnung</mat-label>
          <input matInput [(ngModel)]="material.bezeichnung" [readonly]="readOnly" [disabled]="saving"
                 [maxlength]="maxFreiesMaterialBezeichnungLength" placeholder="z.B. Kabelbinder schwarz">
          <mat-icon matPrefix class="tb-add-icon">inventory_2</mat-icon>
        </mat-form-field>

        <mat-form-field appearance="outline" class="tb-frei-menge">
          <mat-label>Menge</mat-label>
          <input matInput type="number" min="0.0001" step="0.01" inputmode="decimal"
                 [(ngModel)]="material.menge" [readonly]="readOnly" [disabled]="saving">
        </mat-form-field>

        <mat-form-field appearance="outline" class="tb-frei-einheit">
          <mat-label>Einheit</mat-label>
          <input matInput [(ngModel)]="material.einheit" [readonly]="readOnly" [disabled]="saving"
                 placeholder="optional">
        </mat-form-field>

        <button mat-icon-button *ngIf="!readOnly" type="button" (click)="removeFreiesMaterial(b, material)"
                [disabled]="saving" matTooltip="Freies Material entfernen" aria-label="Freies Material entfernen">
          <mat-icon>delete_outline</mat-icon>
        </button>
      </div>

      <p class="sk-muted tb-empty"
         *ngIf="b.lvPositionen.length === 0 && b.freieMaterialien.length === 0">
        Noch kein Material erfasst.
      </p>

      <!-- LV-Live-Suche + freies Material hinzufügen -->
      <div class="tb-lv-search" *ngIf="!readOnly">

        <mat-form-field appearance="outline" class="tb-lv-search-field">
          <mat-label>LV durchsuchen</mat-label>
          <input matInput [ngModel]="b.lvSearch" (ngModelChange)="onLvSearchInput(b, $event)"
                 [disabled]="saving || !activeLv" placeholder="Nummer, Bezeichnung oder Stichwort">
          <mat-icon matPrefix class="tb-add-icon">search</mat-icon>
          <mat-spinner matSuffix *ngIf="b.lvSearchLoading" diameter="18"></mat-spinner>
          <button mat-icon-button matSuffix type="button" *ngIf="b.lvSearch && !b.lvSearchLoading"
                  (click)="clearLvSearch(b)" matTooltip="Suche zurücksetzen" aria-label="Suche zurücksetzen">
            <mat-icon>close</mat-icon>
          </button>
        </mat-form-field>

        <button mat-stroked-button type="button" (click)="addFreiesMaterial(b)" [disabled]="saving">
          <mat-icon>add</mat-icon>
          Freies Material
        </button>
      </div>

      <!-- Kein aktives LV vorhanden -->
      <div class="tb-lv-no-list sk-muted" *ngIf="!readOnly && !activeLv && !activeLvLoading">
        <mat-icon class="sk-inline-icon">info_outline</mat-icon>
        Für diesen Kunden ist kein aktives Leistungsverzeichnis hinterlegt.
        Freies Material kann trotzdem erfasst werden.
      </div>

      <!-- Suchergebnisse -->
      <div class="tb-lv-results" *ngIf="!readOnly && b.lvSearchResults.length > 0">

        <div class="tb-lv-result" *ngFor="let position of b.lvSearchResults">

          <div class="tb-lv-result-info">
            <div class="tb-lv-result-title">
              <strong>{{ position.nummer }}</strong>
              <span class="tb-lv-haupttext">{{ lvHauptText(position) }}</span>
            </div>
            <div class="tb-lv-meta">
              <span *ngIf="lvZusatzText(position) as zusatz">{{ zusatz }}</span>
              <span *ngIf="position.einheit">{{ position.einheit }}</span>
            </div>
          </div>

          <button mat-stroked-button type="button" (click)="addLvPosition(b, position)" [disabled]="saving">
            <mat-icon>add</mat-icon>
            Hinzufügen
          </button>
        </div>
      </div>

      <!-- Keine Treffer -->
      <p class="sk-muted tb-empty"
         *ngIf="!readOnly && b.lvSearchTouched && !b.lvSearchLoading && b.lvSearchResults.length === 0">
        Keine passenden LV-Positionen gefunden.
      </p>


      <!-- ===================================================== -->
      <!-- BESTEHENDE BEMERKUNG BLEIBT ERHALTEN                  -->
      <!-- Nicht dasselbe wie freies Material.                   -->
      <!-- ===================================================== -->

      <mat-form-field appearance="outline" class="tb-full tb-material">
        <mat-label>Zusätzliche Material-/Arbeitsbemerkung</mat-label>

        <textarea matInput [(ngModel)]="b.materialText" [readonly]="readOnly" rows="2" cdkTextareaAutosize>
  </textarea>
      </mat-form-field>


      <div class="tb-errors" *ngIf="showErrors && !readOnly && bereichErrors(b).length > 0">

        <mat-icon class="sk-inline-icon">
          error_outline
        </mat-icon>

        <span>
          Zum Abschließen fehlt:
          {{ bereichErrors(b).join(', ') }}.
        </span>

      </div>

    </section>

    <button mat-stroked-button class="tb-add-bereich" *ngIf="!readOnly" (click)="addBereich()" [disabled]="saving">
      <mat-icon>add</mat-icon> Arbeitsbereich hinzufügen
    </button>

    <div class="tb-total" *ngIf="bereiche.length > 0">
      Gesamt: <strong>{{ formatHours(totalHours) }} h</strong>
    </div>
  </ng-container>
</mat-dialog-content>

<mat-dialog-actions class="tb-actions">
  <button mat-button (click)="close()" [disabled]="saving">Schließen</button>
  <span class="tb-spacer"></span>
  <ng-container *ngIf="!readOnly && !loading">
    <button mat-stroked-button (click)="saveDraft()" [disabled]="saving">
      <mat-icon>save</mat-icon> Entwurf speichern
    </button>
    <button mat-raised-button color="primary" (click)="submit()" [disabled]="saving">
      <mat-icon>task_alt</mat-icon> Abschließen
    </button>
  </ng-container>
</mat-dialog-actions>